import { useEffect, useRef, useState } from "react";

/**
 * A few coarse, honest labels for "the answer isn't back yet" — not a real
 * progress report (the chat call is a single blocking request with no
 * streaming channel telling the frontend which tool is actually running),
 * just enough variety that a slow answer doesn't sit on one static word the
 * whole time it's waiting. Deliberately generic rather than naming a real
 * tool, since claiming to know what's happening server-side when we don't
 * would be exactly the kind of unearned confidence DimeAI itself needs to
 * avoid in its answers.
 */
const STAGES = [
  "Thinking…",
  "Searching the ledger…",
  "Checking the numbers…",
  "Reading the results…",
  "Putting the answer together…",
];

//: How long each label stays up before advancing. Long enough to read
//: without feeling twitchy, short enough that a several-second wait still
//: shows two or three different stages rather than looking stuck. Nudged up
//: slightly from 2200 -- the original felt a touch too fast to actually read.
const STAGE_INTERVAL_MS = 2500;

/**
 * Once the honest stage labels above have had one full lap (a slow answer
 * is clearly taking a while, not just slow to start), we switch to this
 * second rotation -- lighter, self-aware lines about the wait itself,
 * purely to keep someone from assuming DimeAI has hung and navigating
 * away. Not a progress report of any kind, same honesty rule as STAGES.
 * Deliberately no em dashes (renders oddly in a couple of fonts we use)
 * and no franchise/IP references -- light personality, not a meme account.
 */
const LONG_WAIT_QUIPS = [
  "This one has a lot of moving parts, thanks for your patience.",
  "Taking a little longer, because I would rather get it right than get it fast.",
  "Going through the books carefully so the numbers hold up.",
  "Nearly there. Double-checking the figures before I show you.",
  "A bigger question than usual, so I am being thorough.",
  "Still with you. Making sure every number ties back to your books.",
  "Good questions take a little time. I am on it.",
  "Pulling a few things together so the answer is complete.",
  "Thanks for waiting. I want to be sure of this one.",
  "Checking the details so you do not have to.",
];

//: How long a quip stays on screen, whole sentence at once. Long enough to
//: read comfortably (3 to 4 seconds), then it gives way to a plain filler.
const QUIP_HOLD_MS = 3800;

/**
 * Plain, short "still here" lines shown BETWEEN quips, so a long wait reads
 * like a person working rather than a screensaver of back-to-back jokes.
 */
const FILLERS = [
  "Still working on it…",
  "Still on it…",
  "Working through it…",
  "Almost there, still working…",
  "One moment, still checking…",
  "Bear with me, nearly done…",
];

//: How long a filler stays up before the next quip.
const FILLER_HOLD_MS = 2600;

/** A fresh random order of 0..n-1 (Fisher-Yates). */
function shuffledOrder(n: number): number[] {
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

/**
 * Returns the current "what's happening" label while `active` is true.
 * Cycles through `STAGES` once (resetting to the first stage every time
 * `active` goes from false to true, so a new question always starts on
 * "Thinking…"), then alternates a quip (whole sentence, held 3 to 4 seconds)
 * with a plain filler such as "Still working on it…". Quips come in a fresh
 * random order every question, with no repeats until all have been shown, so
 * the first one is not always the same.
 */
export function useThinkingStage(active: boolean): string {
  const [stageIndex, setStageIndex] = useState(0);
  const [phase, setPhase] = useState<"stage" | "quip" | "filler">("stage");
  const [quipPos, setQuipPos] = useState(0);
  const [fillerPos, setFillerPos] = useState(0);
  const wasActive = useRef(false);
  const lapCount = useRef(0);
  const quipOrder = useRef<number[]>(shuffledOrder(LONG_WAIT_QUIPS.length));
  const fillerOrder = useRef<number[]>(shuffledOrder(FILLERS.length));

  useEffect(() => {
    if (!active) {
      wasActive.current = false;
      return undefined;
    }

    if (!wasActive.current) {
      wasActive.current = true;
      lapCount.current = 0;
      quipOrder.current = shuffledOrder(LONG_WAIT_QUIPS.length);
      fillerOrder.current = shuffledOrder(FILLERS.length);
      setStageIndex(0);
      setPhase("stage");
      setQuipPos(0);
      setFillerPos(0);
    }

    if (phase === "stage") {
      const timer = window.setInterval(() => {
        lapCount.current += 1;
        if (lapCount.current >= STAGES.length) {
          setPhase("filler");
          return;
        }
        setStageIndex(lapCount.current);
      }, STAGE_INTERVAL_MS);
      return () => window.clearInterval(timer);
    }

    if (phase === "quip") {
      const timer = window.setTimeout(() => {
        setQuipPos((p) => {
          const next = p + 1;
          if (next >= quipOrder.current.length) {
            quipOrder.current = shuffledOrder(LONG_WAIT_QUIPS.length);
            return 0;
          }
          return next;
        });
        setPhase("filler");
      }, QUIP_HOLD_MS);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => {
      setFillerPos((p) => {
        const next = p + 1;
        if (next >= fillerOrder.current.length) {
          fillerOrder.current = shuffledOrder(FILLERS.length);
          return 0;
        }
        return next;
      });
      setPhase("quip");
    }, FILLER_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [active, phase]);

  if (phase === "quip") {
    return LONG_WAIT_QUIPS[quipOrder.current[quipPos]];
  }
  if (phase === "filler") {
    return FILLERS[fillerOrder.current[fillerPos]];
  }
  return STAGES[stageIndex];
}

export default useThinkingStage;
